/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classNames from 'classnames';
import { replaceCurly } from '@bingads-webui-campaign/fluctuation-common';
import { FluctuationUrlService } from '../fluctuation-url-service';
import { FluctuationLogService } from '../fluctuation-log-service';
import { DetailContainer } from './DetailContainer';

export class ActionContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    const { expandedEntityName = '', entityDetails = [] } = props.defaultEntityDetails || {};
    this.state = {
      entityDetails,
      expandedEntityName,
    };
    this.logService = new FluctuationLogService(props.fromScenario);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.page !== this.props.page) {
      this.resetDetails();
    }
  }

  resetDetails = () => {
    this.setState({
      entityDetails: [],
      expandedEntityName: '',
    }, () => {
      this.props.onDetailContainerShow({
        expandedEntityName: '',
        entityDetails: [],
      });
    });
  };

  replaceAll = (str, search, replacement) => str.replace(new RegExp(search, 'g'), replacement);

  generateHtmlUrl = (param, isHtml) => {
    const urlService = new FluctuationUrlService(this.props.deps);
    let url = urlService.getUrl(param.UrlId);

    if (!url) {
      this.props.deps.userActionActivity.error(`Url Map Not registered! UrlId: ${param.UrlId}`, 'FluctuationError');
      return null;
    }

    url = this.replaceAll(url, '{aid}', this.props.context.AccountId);
    url = this.replaceAll(url, '{cid}', this.props.context.CustomerId);
    url = this.replaceAll(url, '{uid}', this.props.deps.context.CurrentUser.Id);
    url = this.replaceAll(url, '{cmpid}', this.props.context.CampaignId);
    url = this.replaceAll(url, '{adgid}', this.props.context.AdGroupId);

    let completeUrl;
    const helpLinksActions = ['InvoiceAccountPausedActionHelperId', 'InvoiceAccountOnHoldActionHelperId',
      'PrepayAccountPausedActionHelperId', 'ThresholdAccountOnHoldActionHelperId'];

    // learnmore links handle in the if statement
    if (helpLinksActions.indexOf(param.UrlId) > -1) {
      const learnmoreLink = urlService.getUrl('learnmoreLink');
      completeUrl = replaceCurly(
        learnmoreLink,
        this.props.deps.context,
        urlService.getUrl(param.UrlId)
      );
    } else {
      completeUrl = replaceCurly(url, this.props.deps.context, param.Values);
    }

    this.props.deps.userActionActivity.trace(`Url generated: ${url}: ${JSON.stringify(this.props.fluctuation.UniqueId)}`, 'FluctuationAction');

    if (_.isArray(param.EntityScope) && _.size(param.EntityScope) === 2) {
      if (param.EntityScope[0] === 'Campaign') {
        completeUrl = `${completeUrl}&campaignId=${param.EntityScope[1]}`;
      }
    }

    const localizedDisplayName = this.props.deps.i18n.getString(param.DisplayName) ||
      param.DisplayName;
    return isHtml ? completeUrl : `<a href="${completeUrl}" target="_blank">${localizedDisplayName}</a>`;
  };

  generateHtmlString = (message) => {
    const msgStr = this.props.deps.i18n.getString(message.MessageId);
    const substitutes = [];
    message.Parameters.forEach((param) => {
      if (param.Type === 'Url') {
        substitutes.push(this.generateHtmlUrl(param, msgStr.indexOf('href') > -1));
      }
    });

    return replaceCurly(msgStr, null, substitutes);
  };

  flatMap = (arr, fn) => {
    let result = [];
    arr.forEach((item) => {
      const mapping = fn(item);
      result = result.concat(mapping);
    });
    return result;
  };

  generateCause = (cause) => {
    const messageId = cause.Message.Parameters.length > 0 &&
      cause.Message.Parameters[0].Type === 'String' &&
      cause.Message.Parameters[0].Values[0] === '1' ?
      `${cause.Message.MessageId}Singular` :
      cause.Message.MessageId;

    // fall back to original message id in case singular string does not exist
    const tempMessage = this.props.deps.i18n.getString(messageId) ||
      this.props.deps.i18n.getString(cause.Message.MessageId);
    if (tempMessage.indexOf('{0}') > -1) {
      const paramArray = _.map(cause.Message.Parameters, (param, index) => {
        if (param.Type === 'String') {
          const nextParam = cause.Message.Parameters[index + 1];
          return nextParam && nextParam.Type === 'NestUrl' ? '' : param.Values[0];
        }
        if (param.Type === 'Url') {
          return this.generateHtmlUrl(param);
        }
        if (param.Type === 'NestUrl') {
          return this.generateEntityCause({ ...param, messageId: cause.Message.MessageId, key: index });
        }
        return null;
      });

      const finalStr = replaceCurly(
        tempMessage,
        null,
        paramArray
      );
      const pattern = /{\d+}/;
      const regexp = new RegExp(pattern);

      // This if path is only for entity collapsing/expanding root cause
      if (regexp.test(finalStr)) {
        // 1. find object type from param array
        const domIndex = _.findIndex(paramArray, par => typeof (par) === 'object');
        const flattenedFinalStr = this.flatMap(finalStr.split(pattern), part =>
          [part, this.generateEntityCause({
            ...cause.Message.Parameters[domIndex],
            messageId: cause.Message.MessageId,
            key: part,
          })]);
        flattenedFinalStr.pop();
        return flattenedFinalStr;
      }
      return finalStr;
    }
    return tempMessage;
  };

  generateEntityCause = param =>
    (
      <div key={param.DisplayName + param.key}>
        {this.generateSingleEntity(param)}
      </div>
    );

  generateSingleEntity = entity => (
    <button
      className="entity-item"
      onClick={() => {
        this.setState({
          entityDetails: this.state.entityDetails.length > 0 ? [] : entity.SubParameters,
          expandedEntityName: this.props.deps.i18n.getString(entity.Count === 1 ?
            entity.DisplayNameId :
            `${entity.DisplayNameId}Plural`),
        }, () => {
          this.props.onDetailContainerShow({
            expandedEntityName: this.state.expandedEntityName,
            entityDetails: this.state.entityDetails,
          });

          if (this.state.entityDetails.length > 0) {
            this.logService.logContext(
              this.props.fluctuation,
              this.props.date,
              'FluxAction',
              this.props.deps.context.CurrentUser.Id,
              this.props.deps.context.CurrentUser.IsInternal,
              this.props.deps.userActionActivity,
              'FluctuationAction',
              {
                cause: entity.messageId,
                expandedEntityName: this.state.expandedEntityName,
                details: this.state.entityDetails,
              }
            );
            const action = `FluctuationAction${_.isEmpty(this.props.fromScenario) ? '' : `_${this.props.fromScenario}`}`;
            this.props.deps.userActionActivity.trace(
              JSON.stringify(`Expanded entity name: ${this.state.expandedEntityName},
                Expanded entities: ${JSON.stringify(this.state.entityDetails)}`),
              action
            );
          }
        });
      }}
    >
      {`${entity.Count} ${this.props.deps.i18n.getString(entity.Count === 1 ?
          entity.DisplayNameId :
          `${entity.DisplayNameId}Plural`)
      }`}
    </button>
  );

  generateDetailContainer = (cause) => {
    if (this.state.entityDetails && this.state.entityDetails.length > 0) {
      return (
        <DetailContainer
          deps={this.props.deps}
          cause={cause}
          details={this.state.entityDetails}
          onLinkClicked={this.logUserAction}
          context={this.props.fluctuation}
          expandedEntityName={this.state.expandedEntityName}
        />
      );
    }
    return null;
  };

  logUserAction = (action) => {
    /* istanbul ignore else */
    if (action) {
      this.logService.logContext(
        this.props.fluctuation,
        this.props.date,
        'FluxAction',
        this.props.deps.context.CurrentUser.Id,
        this.props.deps.context.CurrentUser.IsInternal,
        this.props.deps.userActionActivity,
        'FluctuationAction',
        {
          action,
        }
      );
    }
  };

  renderActionItem = action => (action.Message.MessageId ? (
    /* eslint-disable jsx-a11y/click-events-have-key-events,
          jsx-a11y/no-static-element-interactions */
    <div
      key={action.Message.MessageId}
      className={classNames('action-list-item', 'action-item', action.Message.NestLevel ? 'nested-item' : '')}
      onClick={() => { this.logUserAction(action); }}
      dangerouslySetInnerHTML={
            {
              __html: this.generateHtmlString(action.Message),
            }
          }
    />
  ) : null);

  render() {
    const causesList = this.props.fluctuation.RootCauses.map((cause) => {
      const formattedCause = this.generateCause(cause);
      if (Array.isArray(formattedCause)) {
        const formattedDom = formattedCause.map((c) => {
          if (typeof (c) === 'string') {
            /* eslint-disable jsx-a11y/click-events-have-key-events,
            jsx-a11y/no-static-element-interactions */
            return (<div
              key={c}
              className="cause-item-segment cause-item"
              onClick={() => { this.logUserAction(cause); }}
              dangerouslySetInnerHTML={
                {
                  __html: c,
                }
              }
            />);
          }
          return (<div key={`${c.key}-${c.type}`} className="entity-item-wrapper">{ c }</div>);
        });

        return (
          <div
            key={cause.RootCauseId}
            className="action-list-item cause-item"
          >
            {formattedDom}
            {this.generateDetailContainer(cause)}
          </div>
        );
      }
      return (<div
        key={cause.Message.MessageId}
        className="action-list-item cause-item"
        onClick={() => { this.logUserAction(cause); }}
        dangerouslySetInnerHTML={
          {
            __html: this.generateCause(cause),
          }
        }
      />);
    });

    let actionsList = this.props.fluctuation.Actions.map((action) => {
      const actionBundle = _.chain(action.Messages)
        .map(message => this.renderActionItem({ Message: message }))
        .compact()
        .value();

      if (!_.isEmpty(actionBundle)) {
        return actionBundle;
      }

      if (!_.isEmpty(action.Message) && !_.isEmpty(action.Message.MessageId)) {
        return this.renderActionItem(action);
      }
      return null;
    });

    actionsList = _.compact(_.flatten(actionsList));

    const causeContainer = this.props.fluctuation.RootCauses.length > 0 ? (
      <div className="action-header">
        {this.props.deps.i18n.getString('Fluctuation_RootCause')}
      </div>) : null;

    const actionContainer = _.isEmpty(actionsList) ? null : (
      <div className="action-header">
        {this.props.deps.i18n.getString('Fluctuation_Actions')}
      </div>);

    // setting inner html as part of payload is coming in as html string
    return (
      <div className="action-container-wrapper">
        {causeContainer}
        {causesList}
        {actionContainer}
        {actionsList}
      </div>
    );
  }
}

ActionContainer.propTypes = {
  page: PropTypes.number.isRequired,
  fluctuation: PropTypes.shape({
    UniqueId: PropTypes.string,
    RootCauses: PropTypes.arrayOf(PropTypes.shape({
      RootCauseId: PropTypes.number,
      Message: PropTypes.shape({
        MessageId: PropTypes.string,
        Parameters: PropTypes.arrayOf(PropTypes.shape({
          DisplayName: PropTypes.string,
          DisplayNameId: PropTypes.string,
          UrlId: PropTypes.string,
          Type: PropTypes.string,
          Values: PropTypes.arrayOf(PropTypes.string),
          SubParameters: PropTypes.arrayOf(PropTypes.shape({
            DisplayName: PropTypes.string,
            DisplayNameId: PropTypes.string,
            UrlId: PropTypes.string,
            Type: PropTypes.string,
            Values: PropTypes.arrayOf(PropTypes.string),
          })),
        })),
      }),
    })).isRequired,
    Actions: PropTypes.arrayOf(PropTypes.shape({
      ActionId: PropTypes.number,
      Message: PropTypes.shape({
        MessageId: PropTypes.string,
        Parameters: PropTypes.arrayOf(PropTypes.shape({
          Type: PropTypes.string,
          UrlId: PropTypes.string,
          DisplayName: PropTypes.string,
          DisplayNameId: PropTypes.string,
          Values: PropTypes.arrayOf(PropTypes.string),
        })),
      }),
    })).isRequired,
  }).isRequired,
  context: PropTypes.shape({
    CustomerId: PropTypes.number,
    AccountId: PropTypes.number,
    CampaignId: PropTypes.number,
    AdGroupId: PropTypes.number,
  }).isRequired,
  date: PropTypes.string.isRequired,
  deps: PropTypes.shape({
    userActionActivity: PropTypes.shape({
      trace: PropTypes.func,
      error: PropTypes.func,
    }),
    context: PropTypes.shape({
      CurrentCustomer: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentAccount: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentUser: PropTypes.shape({
        Id: PropTypes.number,
        IsInternal: PropTypes.bool,
      }),
    }).isRequired,
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }).isRequired,
  }).isRequired,
  defaultEntityDetails: PropTypes.shape({
    expandedEntityName: PropTypes.string,
    entityDetails: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onDetailContainerShow: PropTypes.func,
  fromScenario: PropTypes.string,
};

ActionContainer.defaultProps = {
  onDetailContainerShow: _.noop,
  defaultEntityDetails: {},
  fromScenario: '',
};
